import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import BuyViaDhan from "@/components/BuyViaDhan";

/** Best-strike suggestion for the active signal — Δ≈0.5 pick + IV fairness + OI-wall warning.
 *  This is the bridge between the index signal and the actual option you buy. */
export const StrikeSuggest = ({ symbol, direction, entry, stopLoss, target1, target2 }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error
  useEffect(() => {
    let alive = true;
    let attempts = 0;
    if (!symbol || !direction || direction === "neutral") return;
    const p = new URLSearchParams({ symbol, direction });
    if (target2) p.set("target", target2);
    if (entry) p.set("entry", entry);
    if (stopLoss) p.set("stop_loss", stopLoss);
    if (target1) p.set("target_1", target1);
    const fetchIt = async () => {
      attempts += 1;
      try {
        const { data: d } = await api.get(`/options-lab/strike-suggest?${p.toString()}`);
        if (!alive) return;
        if (d?.ok) { setData(d); setState("ok"); return; }
        throw new Error(d?.reason || "chain unavailable");
      } catch (e) {
        if (!alive) return;
        // DHAN chain is throttled (1 req/3s) — retry up to 4x instead of vanishing.
        console.warn(`strike-suggest attempt ${attempts} failed:`, e?.message || e);
        if (attempts < 4) setTimeout(fetchIt, 5000);
        else setState((s) => (s === "ok" ? s : "error"));
      }
    };
    setState((s) => (s === "ok" ? s : "loading"));
    fetchIt();
    return () => { alive = false; };
  }, [symbol, direction, entry, stopLoss, target1, target2]);
  if (!symbol || !direction || direction === "neutral") return null;
  if (!data) {
    return (
      <div className="border border-zinc-800 bg-[#0a0a0a] px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500"
           data-testid="ew-strike-suggest-pending">
        {state === "error" ? "Option chain busy — will retry on next signal refresh" : "◢ Fetching best option strike…"}
      </div>
    );
  }
  const b = data.best;
  const ivr = data.iv_rank || {};
  const plan = data.trade_plan;
  const ivrTone = ivr.label === "cheap" ? "text-[#22C55E]" : ivr.label === "expensive" ? "text-[#EF4444]" : "text-zinc-300";
  return (
    <div className="border border-[#007AFF]/30 bg-[#007AFF]/5 px-3 py-2 space-y-1.5" data-testid="ew-strike-suggest">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
        <span className="text-[#3B9BFF] font-bold">Best strike · {data.expiry}</span>
        {ivr.rank != null
          ? <span className={ivrTone}>IVR {ivr.rank} · {ivr.label}</span>
          : <span className="text-zinc-500">{ivr.note}</span>}
      </div>
      <div className="text-sm font-mono text-white font-bold" data-testid="ew-strike-pick">
        {b.strike} {b.type} <span className="text-zinc-500 font-normal">LTP ₹{b.ltp}</span>
        <span className="text-zinc-400 font-normal text-xs ml-2">Δ {b.delta} · Θ {b.theta}/d · IV {b.iv?.toFixed ? b.iv.toFixed(1) : b.iv}%</span>
      </div>
      {plan && (
        <div className="border-t border-[#007AFF]/20 pt-1.5 space-y-1" data-testid="ew-premium-plan">
          <div className="grid grid-cols-4 gap-1 text-[10px] font-mono">
            <div>
              <div className="text-zinc-500 uppercase text-[8px] tracking-widest">Entry {plan.mode === "limit_order" ? "(limit)" : ""}</div>
              <div className="text-[#3B9BFF] font-bold" data-testid="ew-premium-entry">₹{plan.limit_entry}</div>
            </div>
            {plan.sl_premium != null && (
              <div>
                <div className="text-zinc-500 uppercase text-[8px] tracking-widest">SL prem</div>
                <div className="text-[#EF4444] font-bold">₹{plan.sl_premium}</div>
              </div>
            )}
            {plan.t1_premium != null && (
              <div>
                <div className="text-zinc-500 uppercase text-[8px] tracking-widest">T1 prem</div>
                <div className="text-[#22C55E] font-bold">₹{plan.t1_premium}</div>
              </div>
            )}
            {plan.t2_premium != null && (
              <div>
                <div className="text-zinc-500 uppercase text-[8px] tracking-widest">T2 prem {plan.premium_rr ? `(RR ${plan.premium_rr})` : ""}</div>
                <div className="text-[#22C55E] font-bold">₹{plan.t2_premium}</div>
              </div>
            )}
          </div>
          <div className={`text-[10px] leading-snug ${plan.mode === "limit_order" ? "text-[#F59E0B]" : "text-zinc-500"}`} data-testid="ew-premium-note">
            {plan.note}
          </div>
        </div>
      )}
      {data.wall_warning && (
        <div className="text-[10px] text-[#F59E0B] leading-snug" data-testid="ew-wall-warning">⚠ {data.wall_warning}</div>
      )}
      <BuyViaDhan symbol={symbol} strike={b.strike} optType={b.type} expiry={data.expiry}
                  suggestedPrice={plan?.limit_entry ?? b.ltp} />
    </div>
  );
};
